<!--
  OnboardingChecklist.vue

  Floating getting-started checklist, pinned bottom-right. Coexists with the
  guided tour: tasks auto-complete from real data (with manual tick/untick), and
  each unfinished task has a "Show me" button that jumps the tour to that step.

  Mounted once at the app root (App.vue). Hidden while the tour overlay is open,
  once every task is done, or after the user dismisses it.
-->
<template>
  <Teleport to="body">
    <div v-if="visible" class="onbc">
      <!-- Collapsed launcher -->
      <button v-if="!store.checklistExpanded" class="onbc-launcher" type="button" @click="store.toggleChecklist()">
        <span class="onbc-ring" :style="ringStyle">
          <span class="onbc-ring-label">{{ completedCount }}/{{ total }}</span>
        </span>
        <span class="onbc-launcher-text">
          <span class="onbc-launcher-title">Getting started</span>
          <span class="onbc-launcher-sub">{{ total - completedCount }} step{{ total - completedCount === 1 ? '' : 's' }} left</span>
        </span>
      </button>

      <!-- Expanded panel -->
      <div v-else class="onbc-panel">
        <div class="onbc-head">
          <div>
            <h3 class="onbc-title">Getting started</h3>
            <p class="onbc-sub">{{ completedCount }} of {{ total }} complete</p>
          </div>
          <div class="onbc-head-actions">
            <button class="onbc-icon-btn" type="button" title="Collapse" @click="store.toggleChecklist()">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
                stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6" /></svg>
            </button>
            <button class="onbc-icon-btn" type="button" title="Dismiss" @click="onDismiss">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
                stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
            </button>
          </div>
        </div>

        <div class="onbc-progress-track"><div class="onbc-progress-fill" :style="{ width: progress + '%' }" /></div>

        <ul class="onbc-list">
          <li v-for="item in items" :key="item.id" class="onbc-item" :class="{ 'onbc-item--done': item.done }">
            <button
              class="onbc-check"
              type="button"
              :aria-pressed="item.done"
              :title="item.done ? 'Mark as not done' : 'Mark as done'"
              @click="toggle(item)"
            >
              <svg v-if="item.done" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
            </button>
            <div class="onbc-item-body">
              <div class="onbc-item-label">{{ item.label }}</div>
              <div class="onbc-item-desc">{{ item.description }}</div>
            </div>
            <button v-if="!item.done" class="onbc-show" type="button" @click="showMe(item)">Show me</button>
          </li>
        </ul>

        <div class="onbc-footer">
          <button class="onbc-foot-btn" type="button" @click="replay">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
              stroke-linecap="round" stroke-linejoin="round"><path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
            Replay tour
          </button>
          <button class="onbc-foot-btn" type="button" @click="openHelp">Help &amp; guide →</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useOnboardingStore } from '../../stores/onboarding'
import { useAuthStore } from '../../stores/auth'
import { useOnboardingChecklist, type ChecklistItem } from '../../composables/useOnboardingChecklist'

const store = useOnboardingStore()
const authStore = useAuthStore()
const router = useRouter()

const { items, completedCount, total, allComplete, progress, settled } = useOnboardingChecklist()

const userId = computed(() => (authStore.currentUser as { id?: string | number } | null)?.id ?? null)

// Load persisted checklist state once we know who the user is.
watch(
  () => [authStore.isAuthenticated, userId.value] as const,
  ([authed]) => {
    if (authed) store.initChecklist(userId.value)
  },
  { immediate: true },
)

// Show only for authenticated users with outstanding steps, while the tour
// overlay isn't open. Wait for data to settle to avoid a "0 of N" flash.
const visible = computed(
  () =>
    authStore.isAuthenticated &&
    !store.checklistDismissed &&
    !store.isActive &&
    settled.value &&
    !allComplete.value,
)

// Once everything is genuinely complete, stop showing (and stop querying) for good.
watch(
  () => settled.value && allComplete.value,
  (done) => {
    if (done && !store.checklistDismissed) store.dismissChecklist(userId.value)
  },
)

const ringStyle = computed(() => ({
  background: `conic-gradient(var(--color-primary-500, #4f8646) ${progress.value * 3.6}deg, #e2e8f0 0deg)`,
}))

const toggle = (item: ChecklistItem) => store.setTaskOverride(userId.value, item.id, !item.done)

const onDismiss = () => store.dismissChecklist(userId.value)

const showMe = (item: ChecklistItem) => {
  store.checklistExpanded = false
  store.startAtStep(item.tourStepId)
}

const replay = () => {
  store.checklistExpanded = false
  router.push('/home').then(() => store.replay())
}

const openHelp = () => {
  store.checklistExpanded = false
  router.push('/help')
}
</script>

<style scoped>
.onbc {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 3000;
  font-family: 'Inter', sans-serif;
}

/* ── Launcher ── */
.onbc-launcher {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 10px 16px 10px 12px;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.16);
  transition: transform 0.15s, box-shadow 0.15s;
}
.onbc-launcher:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.22);
}
.onbc-ring {
  position: relative;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  flex-shrink: 0;
}
.onbc-ring::after {
  content: '';
  position: absolute;
  inset: 4px;
  background: #fff;
  border-radius: 50%;
}
.onbc-ring-label {
  position: relative;
  z-index: 1;
  font-size: 11px;
  font-weight: 700;
  color: #334155;
}
.onbc-launcher-text {
  display: flex;
  flex-direction: column;
  text-align: left;
  line-height: 1.25;
}
.onbc-launcher-title {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
}
.onbc-launcher-sub {
  font-size: 11.5px;
  color: #64748b;
}

/* ── Panel ── */
.onbc-panel {
  width: 340px;
  max-width: calc(100vw - 32px);
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 16px 44px rgba(15, 23, 42, 0.22);
  padding: 16px 16px 12px;
  animation: onbc-pop 0.18s ease both;
}
@keyframes onbc-pop {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }
}
.onbc-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 12px;
}
.onbc-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}
.onbc-sub {
  font-size: 12px;
  color: #64748b;
  margin: 2px 0 0;
}
.onbc-head-actions {
  display: flex;
  gap: 2px;
}
.onbc-icon-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  padding: 4px;
  border-radius: 8px;
  display: grid;
  place-items: center;
}
.onbc-icon-btn:hover {
  background: #f1f5f9;
  color: #475569;
}

.onbc-progress-track {
  height: 6px;
  background: #eef2f6;
  border-radius: 999px;
  overflow: hidden;
  margin-bottom: 12px;
}
.onbc-progress-fill {
  height: 100%;
  background: var(--color-primary-500, #4f8646);
  border-radius: 999px;
  transition: width 0.3s ease;
}

.onbc-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.onbc-item {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 8px 6px;
  border-radius: 10px;
}
.onbc-item:hover {
  background: #f8fafc;
}
.onbc-check {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid #cbd5e1;
  background: #fff;
  cursor: pointer;
  display: grid;
  place-items: center;
  color: #fff;
  transition: all 0.15s;
}
.onbc-item--done .onbc-check {
  background: var(--color-primary-500, #4f8646);
  border-color: var(--color-primary-500, #4f8646);
}
.onbc-item-body {
  flex: 1;
  min-width: 0;
}
.onbc-item-label {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
}
.onbc-item--done .onbc-item-label {
  color: #94a3b8;
  text-decoration: line-through;
}
.onbc-item-desc {
  font-size: 11.5px;
  color: #94a3b8;
  margin-top: 1px;
}
.onbc-item--done .onbc-item-desc {
  display: none;
}
.onbc-show {
  flex-shrink: 0;
  background: var(--color-primary-50, #edf3ec);
  color: var(--color-primary-600, #3f6e38);
  border: none;
  border-radius: 8px;
  padding: 5px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s;
}
.onbc-show:hover {
  background: var(--color-primary-100, #dce7da);
}

.onbc-footer {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
  border-top: 1px solid #f1f5f9;
  padding-top: 8px;
}
.onbc-foot-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: none;
  padding: 4px 2px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  font-family: inherit;
}
.onbc-foot-btn:hover {
  color: var(--color-primary-600, #3f6e38);
}
</style>
